import { ApolloClient, InMemoryCache, createHttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import {
  GET_PRESIGNED_URLS_QUERY,
  GET_LIBRARY_CARD_QUERY,
  GET_STARTUP_BY_USER_QUERY,
  LIST_LIBRARY_CARDS_QUERY,
  SAVE_LIBRARY_CARD_MUTATION,
  PUT_FILE_EVENT_MUTATION,
} from "../queries/libraryCardQueries";

import { GET_LIBRARY_CARD_INDEX_TAGS_QUERY } from "../queries/getLibraryCardIndexTagsQuery";
import { getAuthToken, clearAuthState } from "../utils/authUtils";

// NOTE: cloned from x20-creative. Point this at the x20-founder AppSync endpoint
// once the backend is provisioned.
const GRAPHQL_ENDPOINT =
  "https://m2jxbyhagffjthxtsiuwb2iiwe.appsync-api.us-east-1.amazonaws.com/graphql";

const FALLBACK_API_KEY = null;

// Create HTTP link
const httpLink = createHttpLink({
  uri: GRAPHQL_ENDPOINT,
});

// Create auth link that adds JWT token to headers
const authLink = setContext((_, { headers }) => {
  const token = getAuthToken();

  if (token) {
    console.log("libraryCardApi - Adding Authorization header to request");
  } else {
    console.error("libraryCardApi - No valid JWT token found!");
  }

  // For AWS AppSync with Cognito, use token without "Bearer" prefix
  let authHeaders = {};

  if (token) {
    authHeaders = { Authorization: token };
  } else if (FALLBACK_API_KEY) {
    authHeaders = { "x-api-key": FALLBACK_API_KEY };
  }

  return {
    headers: {
      ...headers,
      ...authHeaders,
    },
  };
});

// Create error link to handle 401 Unauthorized (expired tokens)
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, extensions }) => {
      if (
        extensions?.errorType === "UnauthorizedException" ||
        message.includes("Unauthorized")
      ) {
        console.error(
          "libraryCardApi - Unauthorized error detected, token may be expired",
        );
        clearAuthState();
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    });
  }

  if (networkError) {
    if (networkError.statusCode === 401) {
      console.error(
        "libraryCardApi - 401 Unauthorized network error, token may be expired",
      );
      clearAuthState();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
  }
});

const client = new ApolloClient({
  link: errorLink.concat(authLink).concat(httpLink),
  cache: new InMemoryCache(),
});

export const fetchLibraryCardFromApi = async (input) => {
  try {
    const apiPayload = {
      query: GET_LIBRARY_CARD_QUERY,
      variables: { input },
      fetchPolicy: "network-only",
    };
    const response = await client.query(apiPayload);

    // Check for GraphQL errors
    if (response.errors && response.errors.length > 0) {
      console.error(`libraryCardApi - GraphQL errors:`, response.errors);
      if (response?.data?.getLibraryCard === null) {
        return null;
      }
    }

    return response?.data?.getLibraryCard?.data;
  } catch (error) {
    console.log(`libraryCardApi - error is: ${JSON.stringify(error)}`);
  }
};

export const listLibraryCardsFromApi = async (input) => {
  const apiPayload = {
    query: LIST_LIBRARY_CARDS_QUERY,
    variables: { input },
    fetchPolicy: "network-only",
  };
  const response = await client.query(apiPayload);
  return response.data.listLibraryCards;
};

export const saveLibraryCardToApi = async (input) => {
  const apiPayload = {
    mutation: SAVE_LIBRARY_CARD_MUTATION,
    variables: { input },
  };
  const response = await client.mutate(apiPayload);
  return response;
};

export const createLibraryCardInApi = saveLibraryCardToApi;
export const updateLibraryCardInApi = saveLibraryCardToApi;

export const deleteLibraryCardFromApi = async (input) => {
  console.log("deleteLibraryCardFromApi not implemented yet");
  throw new Error("deleteLibraryCardFromApi not implemented yet");
};

export const fetchPresignedUrlsFromApi = async (actionPayload) => {
  const payload = {
    ...actionPayload,
    operationType: actionPayload.operationType || "GET_PRESIGNED_URLS",
  };

  if (!payload.entityType || typeof payload.entityType !== "string") {
    throw new Error("entityType is required and must be a string");
  }

  if (!payload.tenantId || typeof payload.tenantId !== "string") {
    throw new Error("tenantId is required and must be a string");
  }

  if (!Array.isArray(payload.fileNames) || payload.fileNames.length === 0) {
    throw new Error("fileNames is required and must be a non-empty array");
  }

  const invalidFileNames = payload.fileNames.filter(
    (name) => !name || typeof name !== "string" || name.trim().length === 0,
  );
  if (invalidFileNames.length > 0) {
    throw new Error("All fileNames must be non-empty strings");
  }

  const apiPayload = {
    query: GET_PRESIGNED_URLS_QUERY,
    variables: {
      input: payload,
    },
    fetchPolicy: "no-cache", // presigned URLs must never be served from cache
  };

  try {
    const response = await client.query(apiPayload);
    return response.data.getPresignedUrls.presignedUrls;
  } catch (error) {
    console.error("libraryCardApi - Error fetching presigned URLs:", error);
    throw error;
  }
};

export const putFileEventToApi = async (input) => {
  const apiPayload = {
    mutation: PUT_FILE_EVENT_MUTATION,
    variables: { input },
  };
  const response = await client.mutate(apiPayload);
  return response.data.putFileEvent;
};

export const uploadFileToApi = putFileEventToApi;

export const fetchMetadataFromApi = async (input) => {
  console.log("fetchMetadataFromApi not implemented yet");
  throw new Error("fetchMetadataFromApi not implemented yet");
};

export const fetchLibraryCardDetailsFromApi = fetchLibraryCardFromApi;

export const fetchLibraryCardsFromApi = async (input) => {
  try {
    const apiPayload = {
      query: LIST_LIBRARY_CARDS_QUERY,
      variables: { input },
      fetchPolicy: "network-only",
    };
    const response = await client.query(apiPayload);
    return response.data.listLibraryCards;
  } catch (error) {
    console.error("libraryCardApi - fetchLibraryCardsFromApi - error:", error);
    throw error;
  }
};

// Fetch STARTUPRECORD by Cognito userId — uses CreatedByIndex GSI on the backend
export const fetchStartupByUserFromApi = async (userId) => {
  try {
    const response = await client.query({
      query: GET_STARTUP_BY_USER_QUERY,
      variables: { input: { operationType: "GET_STARTUP_BY_USER", userId } },
      fetchPolicy: "network-only",
    });
    return response?.data?.getStartupByUser ?? null;
  } catch (error) {
    console.error(`fetchStartupByUserFromApi error: ${error.message}`);
    return null;
  }
};

export const getLibraryCardTagsApi = async (payload) => {
  try {
    const response = await client.query({
      query: GET_LIBRARY_CARD_INDEX_TAGS_QUERY,
      variables: { input: payload },
    });
    return response;
  } catch (error) {
    console.error(`libraryCardApi - Error fetching tags: ${error.message}`);
    throw error;
  }
};
